import React from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Chip,
  Box,
} from "@mui/material";
import {
  EarthwormFoundationRiskLevel,
  EARTHWORM_DISPLAY_TEXT,
  EARTHWORM_CATEGORY_LABELS,
} from "../../constants/earthworm";
import { RISK_COLORS, TEXT_COLORS } from "../../constants/colors";

const EARTHWORM_RISK_COLOR_MAP = {
  [EarthwormFoundationRiskLevel.LOW]: RISK_COLORS.LOW,
  [EarthwormFoundationRiskLevel.MEDIUM]: RISK_COLORS.MEDIUM,
  [EarthwormFoundationRiskLevel.HIGH]: RISK_COLORS.HIGH,
  [EarthwormFoundationRiskLevel.INSUFFICIENT_DATA]: RISK_COLORS.INSUFFICIENT_DATA,
} as const;

interface EarthwormFoundationData {
  legality: string | null;
  forest_degradation_hcv: string | null;
  deforestation: string | null;
  indigenous_rights: string | null;
  labour_rights: string | null;
}

interface EarthwormFoundationRiskTableProps {
  data: EarthwormFoundationData;
}

function RiskChip({ value }: { value: string | null }) {
  if (value === null) return null;

  const riskLevel = value as EarthwormFoundationRiskLevel;
  const backgroundColor = EARTHWORM_RISK_COLOR_MAP[riskLevel] || RISK_COLORS.INSUFFICIENT_DATA;
  const textColor = riskLevel === EarthwormFoundationRiskLevel.MEDIUM ? TEXT_COLORS.PRIMARY : "white";

  return (
    <Box display="flex" justifyContent="center" alignItems="center">
      <Chip
        label={EARTHWORM_DISPLAY_TEXT[riskLevel]}
        sx={{
          backgroundColor,
          color: textColor,
          fontWeight: "500",
          fontSize: "14px",
          minWidth: "140px",
          height: "36px",
        }}
      />
    </Box>
  );
}

export default function EarthwormFoundationRiskTable({
  data,
}: EarthwormFoundationRiskTableProps) {
  const categories = [
    { key: 'legality', label: EARTHWORM_CATEGORY_LABELS.legality },
    { key: 'forest_degradation_hcv', label: EARTHWORM_CATEGORY_LABELS.forest_degradation_hcv },
    { key: 'deforestation', label: EARTHWORM_CATEGORY_LABELS.deforestation },
    { key: 'indigenous_rights', label: EARTHWORM_CATEGORY_LABELS.indigenous_rights },
    { key: 'labour_rights', label: EARTHWORM_CATEGORY_LABELS.labour_rights },
  ] as const;

  return (
    <TableContainer sx={{ borderRadius: "10px" }}>
      <Table size="small" aria-label="earthworm foundation risk table">
        <TableBody>
          {categories.map(({ key, label }) => (
            <TableRow key={key} sx={{ height: "60px" }}>
              <TableCell sx={{ width: "40%" }}>{label}</TableCell>
              <TableCell
                sx={{
                  width: "20%",
                  textAlign: "center",
                  "&:hover": { cursor: "default" },
                }}
              >
                <RiskChip value={data[key]} />
              </TableCell>
              <TableCell align="right" sx={{ width: "40%" }}></TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
